<template>
  <transition name="fade-fab">
    <button
      v-if="showFab"
      type="button"
      class="floating-report-fab"
      @click="handleClick"
      :title="$t('mainuser.createNewRequest')"
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
      </svg>
      <span class="fab-label">{{ $t('mainuser.createNewRequest') }}</span>
    </button>
  </transition>
</template>

<script>
import Swal from "sweetalert2";
import Service from "@/service/api";

export default {
  name: "FloatingReportFab",
  data() {
    return {
      isDesktop: false,
      hasTopButton: false,
      isTopButtonVisible: true,
      _scrollContainers: [],
      _scrollHandler: null,
      _resizeHandler: null,
      _observer: null,
      _mutationObserver: null,
    };
  },
  computed: {
    isNewRequestPage() {
      return this.$route && this.$route.path && this.$route.path.includes("/new-request");
    },
    showFab() {
      if (this.isDesktop) return false;
      if (this.isNewRequestPage) return false;
      // If page has a top button, only show when top button is scrolled out of view
      if (this.hasTopButton) {
        return !this.isTopButtonVisible;
      }
      // If page does not have a top button (History, Manual, etc.), always show
      return true;
    },
    currentRole() {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          if (user && user.role) {
            if (user.role === "student_affairs") return "student-affairs";
            return user.role;
          }
        } catch (e) {
          // ignore
        }
      }
      if (this.$route && this.$route.path) {
        const segment = this.$route.path.split("/")[1];
        if (["admin", "supervisor", "technician", "user", "student-affairs"].includes(segment)) {
          return segment;
        }
      }
      return "user";
    },
  },
  watch: {
    $route() {
      if (!this.isDesktop) {
        this.$nextTick(() => {
          this.detectTopButtonAndObserve();
        });
      }
    },
  },
  mounted() {
    this.checkScreenSize();
    this._resizeHandler = () => this.checkScreenSize();
    window.addEventListener("resize", this._resizeHandler, { passive: true });

    if (!this.isDesktop) {
      this.$nextTick(() => {
        this.detectTopButtonAndObserve();
      });
    }

    // Also observe DOM changes in case child components finish rendering async
    if (typeof MutationObserver !== "undefined") {
      this._mutationObserver = new MutationObserver(() => {
        if (!this.isDesktop) {
          this.checkTopButtonVisibility();
        }
      });
      this._mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }
  },
  beforeDestroy() {
    if (this._resizeHandler) {
      window.removeEventListener("resize", this._resizeHandler);
      this._resizeHandler = null;
    }
    this.teardownListeners();
    if (this._mutationObserver) {
      this._mutationObserver.disconnect();
      this._mutationObserver = null;
    }
  },
  methods: {
    checkScreenSize() {
      if (typeof window === "undefined") return;
      const wasDesktop = this.isDesktop;
      this.isDesktop = window.innerWidth > 1200;
      if (this.isDesktop) {
        this.teardownListeners();
      } else if (wasDesktop && !this.isDesktop) {
        this.$nextTick(() => {
          this.detectTopButtonAndObserve();
        });
      }
    },
    getTopButtonElement() {
      return document.querySelector(
        ".dashboard-topbar .report-btn, .dashboard-topbar .hero-action, .dashboard-filter .report-btn, .dashboard-filter .hero-action, .hero-card .report-btn, .staff-ui-header .report-btn, .hero-action-btn"
      );
    },
    detectTopButtonAndObserve() {
      this.teardownListeners();
      if (this.isDesktop) {
        return;
      }

      const topBtn = this.getTopButtonElement();
      if (!topBtn) {
        this.hasTopButton = false;
        this.isTopButtonVisible = false;
        return;
      }

      this.hasTopButton = true;
      this._scrollHandler = () => this.checkTopButtonVisibility();

      this._scrollContainers = [
        window,
        document,
        document.documentElement,
        document.body,
        document.querySelector(".c-body"),
        document.querySelector(".c-main"),
        document.querySelector(".c-app"),
        document.querySelector(".c-wrapper"),
      ].filter(Boolean);

      this._scrollContainers.forEach((el) => {
        el.addEventListener("scroll", this._scrollHandler, { passive: true });
      });

      if (typeof IntersectionObserver !== "undefined") {
        this._observer = new IntersectionObserver((entries) => {
          const entry = entries[0];
          if (entry) {
            this.isTopButtonVisible = entry.isIntersecting;
          }
        }, { threshold: 0.1 });
        this._observer.observe(topBtn);
      }

      this.checkTopButtonVisibility();
    },
    checkTopButtonVisibility() {
      if (this.isDesktop) return;
      const topBtn = this.getTopButtonElement();
      if (!topBtn) {
        this.hasTopButton = false;
        this.isTopButtonVisible = false;
        return;
      }
      this.hasTopButton = true;
      const rect = topBtn.getBoundingClientRect();
      // Button is visible if its bottom is below top of viewport and top is above bottom of viewport
      this.isTopButtonVisible = rect.bottom > 10 && rect.top < (window.innerHeight || document.documentElement.clientHeight);
    },
    teardownListeners() {
      if (this._scrollHandler) {
        (this._scrollContainers || []).forEach((el) => {
          el.removeEventListener("scroll", this._scrollHandler);
        });
      }
      this._scrollContainers = [];
      this._scrollHandler = null;
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
    },
    async handleClick() {
      const role = this.currentRole;
      const targetRoute = `/${role}/new-request`;

      if (role !== "user") {
        if (this.$route.path !== targetRoute) {
          this.$router.push(targetRoute);
        }
        return;
      }

      const userStr = localStorage.getItem("user");
      const userId = userStr ? JSON.parse(userStr)._id : null;
      if (!userId) {
        if (this.$route.path !== targetRoute) {
          this.$router.push(targetRoute);
        }
        return;
      }

      try {
        const response = await Service.ticket("get", null, {
          urlAppend: `/user/${userId}/active`,
        });
        if (response.status === 200) {
          const data = response.data;
          if (data.success && Array.isArray(data.data)) {
            const blockingTicket = data.data.find(
              (ticket) => ticket.status === "done"
            );
            if (blockingTicket) {
              Swal.fire({
                icon: "warning",
                title: this.$t("mainuser.evalBeforeNewTitle"),
                text: this.$t("mainuser.evalBeforeNewText"),
                confirmButtonColor: "rgba(37, 99, 235, 0.85)",
                confirmButtonText: this.$t("login.ok"),
                returnFocus: false,
              }).then(() => {
                this.$emit("show-blocking-ticket", blockingTicket);
                if (this.$route.path !== "/user/mainuser") {
                  this.$router.push("/user/mainuser");
                }
              });
              return;
            }
          }
        }
      } catch (err) {
        console.error("Error checking unevaluated tickets", err);
      }

      if (this.$route.path !== targetRoute) {
        this.$router.push(targetRoute);
      }
    },
  },
};
</script>

<style scoped>
.floating-report-fab {
  position: fixed !important;
  bottom: 85px !important;
  right: 20px !important;
  left: auto !important;
  z-index: 1050 !important;
  display: inline-flex;
  align-items: center !important;
  gap: 8px !important;
  padding: 12px 20px !important;
  background: linear-gradient(135deg, #1e40af 0%, #0284c7 100%) !important;
  color: #ffffff !important;
  border: 2px solid #ffffff !important;
  border-radius: 50px !important;
  font-size: 14px !important;
  font-weight: 800 !important;
  box-shadow: 0 8px 25px rgba(2, 132, 199, 0.4) !important;
  cursor: pointer !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease !important;
  user-select: none;
}

.floating-report-fab:hover {
  transform: translateY(-3px) scale(1.03) !important;
  box-shadow: 0 12px 30px rgba(2, 132, 199, 0.5) !important;
  background: linear-gradient(135deg, #1e3a8a 0%, #0369a1 100%) !important;
  color: #ffffff !important;
}

.floating-report-fab:active {
  transform: translateY(-1px) scale(0.98) !important;
}

.fade-fab-enter-active,
.fade-fab-leave-active {
  transition: opacity 0.25s ease, transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.fade-fab-enter,
.fade-fab-leave-to {
  opacity: 0;
  transform: translateY(20px) scale(0.85);
}

@media (min-width: 1201px) {
  .floating-report-fab {
    display: none !important;
  }
}
</style>
